import { platformOf } from "../lib/sourcePlatform";

/**
 * A link out to where a piece of content came from (ADR 000060).
 *
 * Drawn as an icon rather than the URL, because a corpus listing is scanned
 * rather than read and a full YouTube address in every row is noise. The
 * platform's name rides along as the accessible name and the tooltip, so it is
 * identified without being spelled out.
 *
 * Only two marks are drawn: a play button for YouTube and Vimeo's own, both
 * simple enough to render honestly at 16px. Everything else gets a link glyph,
 * because an approximated logo misrepresents somebody's brand and a plain
 * arrow does not.
 */
export function SourceLink({ sourceUrl }: { sourceUrl: string | null | undefined }) {
  const platform = platformOf(sourceUrl);
  if (!platform || !sourceUrl) return null;

  return (
    <a
      className="source-link"
      href={sourceUrl}
      target="_blank"
      rel="noreferrer noopener"
      title={`Open on ${platform.name}`}
      style={platform.brand ? { ["--brand" as string]: platform.brand } : undefined}
      // The row behind this is itself a link to the asset. Without this, a
      // click here would follow both.
      onClick={(event) => event.stopPropagation()}
    >
      <Glyph kind={platform.glyph} />
      <span className="visually-hidden">Open on {platform.name}</span>
    </a>
  );
}

function Glyph({ kind }: { kind: "youtube" | "vimeo" | "link" }) {
  if (kind === "youtube") {
    return (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
        <path
          fill="currentColor"
          d="M23.5 6.5a3 3 0 0 0-2.1-2.1C19.5 4 12 4 12 4s-7.5 0-9.4.4A3 3 0 0 0 .5 6.5 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.5 3 3 0 0 0 2.1 2.1c1.9.4 9.4.4 9.4.4s7.5 0 9.4-.4a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.5Z"
        />
        <path fill="var(--ground)" d="M9.6 15.6 15.8 12 9.6 8.4Z" />
      </svg>
    );
  }

  if (kind === "vimeo") {
    return (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
        <path
          fill="currentColor"
          d="M23 7.4c-.1 2.2-1.7 5.3-4.7 9.2-3.1 4-5.7 6.1-7.9 6.1-1.3 0-2.5-1.3-3.4-3.8L5.2 12C4.5 9.5 3.8 8.2 3 8.2c-.2 0-.8.4-1.8 1.1L0 7.9a289 289 0 0 0 3.4-3c1.5-1.3 2.7-2 3.4-2.1 1.8-.2 2.9 1 3.3 3.7.5 2.9.8 4.7 1 5.4.5 2.4 1.1 3.6 1.7 3.6.5 0 1.2-.7 2.1-2.2.9-1.5 1.4-2.6 1.5-3.4.1-1.3-.4-2-1.5-2-.5 0-1.1.1-1.6.4C13.4 4.6 15.7 2.2 19 2.3c2.5.1 3.6 1.8 3.5 5.1Z"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        d="M10 14a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1M14 10a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1"
      />
    </svg>
  );
}
